import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import LogInquiryModal from './LogInquiryModal';
import InquiryDetailsModal from './InquiryDetailsModal';
import './Dashboard.css';

const STATUS_LABELS = {
  pending: 'Pending',
  researching: 'Researching',
  priced: 'Priced',
  converted: 'Converted',
  declined: 'Declined',
};

const STATUS_COLORS = {
  pending: '#7f8c8d',
  researching: '#FFA500',
  priced: '#8e44ad',
  converted: '#228B22',
  declined: '#c0392b',
};

const InquiriesTab = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);

  const fetchInquiries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/special-orders/inquiries', {
        params: {
          status: filterStatus === 'all' ? '' : filterStatus,
          search: searchTerm,
        },
      });
      setInquiries(response.data.inquiries || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch inquiries');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, searchTerm]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const handleViewInquiry = (inquiry) => {
    setSelectedInquiry(inquiry);
    setIsDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedInquiry(null);
  };

  const openCount = inquiries.filter((i) => !['converted', 'declined'].includes(i.status)).length;
  const pricedCount = inquiries.filter((i) => i.status === 'priced').length;
  const convertedCount = inquiries.filter((i) => i.status === 'converted').length;

  return (
    <div className="special-orders-dashboard">
      <div className="dashboard-header">
        <p>Log customer requests for items we don't stock, price them, and convert to a Special Order.</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h4>Total Inquiries</h4>
          <p className="stat-value">{inquiries.length}</p>
        </div>
        <div className="stat-card">
          <h4>Open</h4>
          <p className="stat-value" style={{ color: '#FFA500' }}>{openCount}</p>
        </div>
        <div className="stat-card">
          <h4>Priced, Awaiting Customer</h4>
          <p className="stat-value" style={{ color: '#8e44ad' }}>{pricedCount}</p>
        </div>
        <div className="stat-card">
          <h4>Converted</h4>
          <p className="stat-value" style={{ color: '#228B22' }}>{convertedCount}</p>
        </div>
      </div>

      <div className="controls-section">
        <div className="controls-top-row">
          <div className="search-filter">
            <input
              type="text"
              placeholder="Search by customer or item..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <button onClick={() => setIsLogModalOpen(true)} className="btn-primary">
            + Log Inquiry
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading inquiries...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : inquiries.length === 0 ? (
        <div className="no-data">
          <p>No inquiries found. Log your first one!</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Phone</th>
                <th>Item Requested</th>
                <th>Status</th>
                <th>Quoted Price</th>
                <th>Logged By</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {inquiries.map((inquiry) => (
                <tr key={inquiry.id}>
                  <td>{inquiry.customer_name}</td>
                  <td>{inquiry.customer_phone || '-'}</td>
                  <td>{inquiry.item_description}</td>
                  <td>
                    <span
                      className="status-badge"
                      style={{ backgroundColor: STATUS_COLORS[inquiry.status] }}
                    >
                      {STATUS_LABELS[inquiry.status] || inquiry.status}
                    </span>
                  </td>
                  <td>{inquiry.quoted_price ? `$${parseFloat(inquiry.quoted_price).toFixed(2)}` : '-'}</td>
                  <td>{inquiry.requested_by_name || '-'}</td>
                  <td>{new Date(inquiry.created_at).toLocaleDateString()}</td>
                  <td>
                    <button onClick={() => handleViewInquiry(inquiry)} className="btn-view">
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InquiryDetailsModal
        inquiry={selectedInquiry}
        isOpen={isDetailsOpen}
        onClose={handleCloseDetails}
        onSuccess={fetchInquiries}
      />

      <LogInquiryModal
        isOpen={isLogModalOpen}
        onClose={() => setIsLogModalOpen(false)}
        onSuccess={fetchInquiries}
      />
    </div>
  );
};

export default InquiriesTab;
